import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import DropdownButton from "src/components/DropdownButton";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import useLimits from "src/hooks/api/services/auth/useLimits";
import useAuth from "src/hooks/selectors/useAuth";
import useModules from "src/hooks/selectors/useModules";

interface PublishTaskButtonProps extends Translation {
	id: string;
	variant: "button" | "dropdown-button";
	button?: {
		small?: boolean;
	};
}

const PublishTaskButton = ({
	t,
	id,
	variant,
	button,
}: PublishTaskButtonProps) => {
	const auth = useAuth();
	const { modules } = useModules();
	const navigate = useNavigate();
	const { limits, status } = useLimits();
	const onClick = () => navigate(`/tasks/${id}/publish`);
	let help = undefined;

	if (!limits?.published_tasks?.is_available) {
		help = t("help.exceeded");
	}

	if (!modules?.includes("extern-task")) {
		help = t("help.subscription");
	}

	if (variant === "dropdown-button") {
		return (
			<DropdownButton.Item
				loading={status !== "idle"}
				icon="far fa-hands-helping"
				disabled={!limits?.published_tasks?.is_available}
				{...{ onClick }}
			>
				{t("publish")}
			</DropdownButton.Item>
		);
	}

	return (
		<Button
			{...{ onClick }}
			onClick={() => (auth.has_subscription ? onClick() : null)}
			to={
				!auth.has_subscription
					? "/subscription"
					: !limits?.published_tasks?.is_available
					? "/profile/billing/change"
					: undefined
			}
			loading={status !== "idle"}
			help={help}
			tabIndex="-1"
			type="primary"
			id="outsource-button"
			{...button}
		>
			{t("publish")}
		</Button>
	);
};

PublishTaskButton.locale = {
	nl: {
		publish: "Uitbesteden",
		help: {
			exceeded:
				"Je hebt het uitbesteed limiet van je abonnement verbruikt",
			subscription:
				"Je hebt een basic of premium abonnement nodig om taken uit te besteden",
		},
	},
	en: {
		publish: "Outsource",
		help: {
			exceeded:
				"You have reached the outsource limit of your subscription",
			subscription:
				"You need a basic or premium subscription to outsource tasks",
		},
	},
};

export default withTranslation(PublishTaskButton);
